import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, Badge, Button, Card, DataTable, EmptyState, LoadingState, type Column } from "@agentnative/ui";
import { useAuthStore } from "../stores/auth";
import { apiErrorMessage } from "../lib/api/client";
import { listSessions, revokeAllSessions, revokeSession } from "../lib/api/auth";
import type { Session } from "../lib/api/types";

export function SessionsRoute() {
  const user = useAuthStore((s) => s.user);
  const queryClient = useQueryClient();

  const sessionsQuery = useQuery({
    queryKey: ["sessions", user?.id],
    queryFn: () => listSessions(user!.id),
    enabled: !!user,
  });

  const revokeMutation = useMutation({
    mutationFn: (sessionId: string) => revokeSession(user!.id, sessionId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["sessions", user?.id] }),
  });

  const revokeAllMutation = useMutation({
    mutationFn: () => {
      const current = sessionsQuery.data?.sessions.find((s) => s.isCurrent);
      return revokeAllSessions(user!.id, current?.id);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["sessions", user?.id] }),
  });

  if (!user) {
    return (
      <EmptyState title="Not signed in" description="Sign in to view your active sessions." />
    );
  }

  if (sessionsQuery.isLoading) {
    return <LoadingState message="Loading sessions..." />;
  }

  if (sessionsQuery.isError) {
    return (
      <Alert variant="danger" title="Failed to load sessions">
        {apiErrorMessage(sessionsQuery.error)}
      </Alert>
    );
  }

  const sessions = sessionsQuery.data?.sessions ?? [];

  const columns: Column<Session>[] = [
    {
      key: "device",
      label: "Device",
      render: (s) => (
        <div className="flex items-center gap-2">
          <span>{s.deviceName || "Unknown device"}</span>
          {s.isCurrent && <Badge variant="success">This device</Badge>}
        </div>
      ),
    },
    { key: "ipAddress", label: "IP address", render: (s) => s.ipAddress || "—" },
    {
      key: "lastActivityAt",
      label: "Last active",
      render: (s) => new Date(s.lastActivityAt).toLocaleString(),
    },
    {
      key: "actions",
      label: "",
      render: (s) =>
        !s.isCurrent && (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => revokeMutation.mutate(s.id)}
            disabled={revokeMutation.isPending}
          >
            Revoke
          </Button>
        ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Active sessions</h1>
        <Button
          variant="secondary"
          onClick={() => revokeAllMutation.mutate()}
          disabled={revokeAllMutation.isPending || sessions.length <= 1}
        >
          Log out other devices
        </Button>
      </div>
      <Card padding="none">
        <DataTable<Session>
          data={sessions}
          columns={columns}
          emptyMessage="No active sessions."
        />
      </Card>
    </div>
  );
}
